import clsx from 'clsx';
import type {MRT_ColumnDef} from 'material-react-table';
import {useCallback, useEffect, useMemo} from 'react';
import translate from '@/utils/translate';
import {COLUMN_ID, type ColumnId} from '@/types/models/creator-crm/columnId';
import {SearchDisplayCreator} from '@/types/models/creator-crm/creatorCrm';
import type {ColumnDefinition} from '@/components/column-selector';
import {
  creatorCrmActions,
  useCreatorCrmStore,
} from '@/stores/creator-crm/creatorCrmStore';
import {shouldShowColumn} from '@/stores/creator-crm/utils/filterVisibilityUtils';

type CreatorTableColumnConfigItem = {
  id: ColumnId;
  minSize: number;
  maxSize: number;
  hideOnYoutube?: boolean;
};

const CREATOR_TABLE_COLUMN_CONFIG: CreatorTableColumnConfigItem[] = [
  {id: COLUMN_ID.select, minSize: 45, maxSize: 45},
  {id: COLUMN_ID.name, minSize: 125, maxSize: 292},
  {id: COLUMN_ID.followers, minSize: 110, maxSize: 292},
  {
    id: COLUMN_ID.engagementRate,
    minSize: 90,
    maxSize: 292,
    hideOnYoutube: true,
  },
  {id: COLUMN_ID.location, minSize: 150, maxSize: 292},
  {id: COLUMN_ID.joined, minSize: 100, maxSize: 292},
  {id: COLUMN_ID.gender, minSize: 80, maxSize: 292},
  {id: COLUMN_ID.age, minSize: 80, maxSize: 292},
  {id: COLUMN_ID.categories, minSize: 125, maxSize: 292},
  {id: COLUMN_ID.socialNetworks, minSize: 150, maxSize: 292},
  {id: COLUMN_ID.vettingDecision, minSize: 224, maxSize: 292},
];

const DEFAULT_MIN_COLUMN_SIZE = 125;
const DEFAULT_MAX_COLUMN_SIZE = 292;

const CREATOR_COLUMN_MIN_SIZES = Object.fromEntries(
  CREATOR_TABLE_COLUMN_CONFIG.map((column) => [column.id, column.minSize])
) as Record<string, number>;

const CREATOR_COLUMN_CONFIG_MAP = Object.fromEntries(
  CREATOR_TABLE_COLUMN_CONFIG.map((column) => [column.id, column])
) as Record<string, CreatorTableColumnConfigItem>;

export type StickyClassNames = {
  pinnedHeaderCell: string;
  pinnedBodyCell: string;
  pinnedColumnShadow: string;
};

type UseCreatorTableColumnsConfigParams = {
  selectedPlatform: string;
  columns: MRT_ColumnDef<SearchDisplayCreator>[];
  stickyClassNames: StickyClassNames;
  isSearchMode?: boolean;
};

export function useCreatorTableColumnsConfig({
  selectedPlatform,
  columns,
  stickyClassNames,
  isSearchMode,
}: UseCreatorTableColumnsConfigParams) {
  const tableColumns = useCreatorCrmStore(
    (state) => state.content.tableColumns
  );

  const isYoutube = selectedPlatform === 'youtube';

  useEffect(() => {
    if (isYoutube) {
      creatorCrmActions.changeTableColumnPin(COLUMN_ID.engagementRate, false);
    }
  }, [isYoutube]);

  const {visibleColumns, userPinnedColumns, lockedColumnIds, pinnedColumns} =
    useMemo(() => {
      const visible: Partial<Record<ColumnId, boolean>> = {};
      const userPinned: ColumnId[] = [];
      const locked: ColumnId[] = [];
      tableColumns.forEach((column) => {
        if (isYoutube && column.id === COLUMN_ID.engagementRate) {
          return;
        }
        visible[column.id] = column.visible;
        if (column.locked) locked.push(column.id);
        else if (column.pinned) userPinned.push(column.id);
      });
      return {
        visibleColumns: visible,
        userPinnedColumns: userPinned,
        lockedColumnIds: locked,
        pinnedColumns: [...locked, ...userPinned] as ColumnId[],
      };
    }, [tableColumns, isYoutube]);

  const columnConfigs: ColumnDefinition<ColumnId>[] = useMemo(() => {
    return tableColumns
      .filter(
        (column) => !(isYoutube && column.id === COLUMN_ID.engagementRate)
      )
      .filter((column) => shouldShowColumn(column.id, isSearchMode))
      .map((column) => ({
        id: column.id,
        label:
          isYoutube && column.id === COLUMN_ID.followers
            ? translate('Subscriber Count')
            : translate(column.localeLabelKey),
        locked: column.locked,
        canPin: column.canPin,
      }));
  }, [tableColumns, isYoutube, isSearchMode]);

  const columnOrder = useMemo(() => {
    const defaultOrder = CREATOR_TABLE_COLUMN_CONFIG.filter(
      (column) =>
        !(isYoutube && 'hideOnYoutube' in column && column.hideOnYoutube) &&
        shouldShowColumn(column.id, isSearchMode)
    ).map((column) => column.id);
    const rest = defaultOrder.filter(
      (id) => !lockedColumnIds.includes(id) && !userPinnedColumns.includes(id)
    );
    return [
      ...lockedColumnIds,
      ...userPinnedColumns.filter((id) => !lockedColumnIds.includes(id)),
      ...rest,
    ];
  }, [isYoutube, isSearchMode, userPinnedColumns, lockedColumnIds]);

  const handleColumnVisibilityChange = useCallback(
    (columnId: ColumnId, visible: boolean) => {
      creatorCrmActions.changeTableColumnVisibility(columnId, visible);
    },
    []
  );

  const handleColumnPinChange = useCallback(
    (columnId: ColumnId, pinned: boolean) => {
      creatorCrmActions.changeTableColumnPin(columnId, pinned);
    },
    []
  );

  const getLeftOffset = useCallback(
    (columnId: ColumnId): number => {
      const pinnedIndex = pinnedColumns.indexOf(columnId);
      if (pinnedIndex === -1) {
        return 0;
      }
      return pinnedColumns
        .slice(0, pinnedIndex)
        .reduce(
          (acc, id) =>
            acc + (CREATOR_COLUMN_MIN_SIZES[id] || DEFAULT_MIN_COLUMN_SIZE),
          0
        );
    },
    [pinnedColumns]
  );

  const getStickyColumnProps = useCallback(
    (columnId: ColumnId, baseAlign: 'left' | 'right' | 'center' = 'left') => {
      const isPinned = pinnedColumns.includes(columnId);
      const colWidth =
        CREATOR_COLUMN_MIN_SIZES[columnId] ?? DEFAULT_MIN_COLUMN_SIZE;
      if (!isPinned) {
        return {
          muiTableHeadCellProps: {align: baseAlign},
          muiTableBodyCellProps: {align: baseAlign},
        };
      }
      const leftOffset = getLeftOffset(columnId);
      const isLastPinned =
        pinnedColumns.indexOf(columnId) === pinnedColumns.length - 1;
      const headerClassName = clsx(
        stickyClassNames.pinnedHeaderCell,
        isLastPinned && stickyClassNames.pinnedColumnShadow
      );
      const bodyClassName = clsx(
        stickyClassNames.pinnedBodyCell,
        isLastPinned && stickyClassNames.pinnedColumnShadow
      );
      const cellStyle = {
        left: leftOffset,
        minWidth: colWidth,
        width: colWidth,
        maxWidth: colWidth,
        boxSizing: 'border-box' as const,
      };
      return {
        muiTableHeadCellProps: {
          align: baseAlign,
          className: headerClassName,
          style: cellStyle,
        },
        muiTableBodyCellProps: {
          align: baseAlign,
          className: bodyClassName,
          style: cellStyle,
        },
      };
    },
    [pinnedColumns, getLeftOffset, stickyClassNames]
  );

  const columnsWithSticky = useMemo(() => {
    return columns.map((column) => {
      if (!column.id) {
        return column;
      }
      const config = CREATOR_COLUMN_CONFIG_MAP[column.id];
      const isPinned = pinnedColumns.includes(column.id as ColumnId);
      const stickyProps = getStickyColumnProps(column.id as ColumnId, 'left');
      return {
        ...column,
        ...stickyProps,
        minSize: config?.minSize ?? DEFAULT_MIN_COLUMN_SIZE,
        maxSize: config?.maxSize ?? DEFAULT_MAX_COLUMN_SIZE,
        size: config?.minSize ?? DEFAULT_MIN_COLUMN_SIZE,
        grow: isPinned ? 0 : 1,
      };
    });
  }, [columns, getStickyColumnProps, pinnedColumns]);

  return {
    columnConfigs,
    pinnedColumns,
    columnOrder,
    visibleColumns,
    handleColumnVisibilityChange,
    handleColumnPinChange,
    columnsWithSticky,
  };
}
